import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, User, Settings, Users, PieChart } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { navigationItems as defaultNavigationItems } from '@/lib/navigation';

interface SearchOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isGlobalDarkMode: boolean;
}

export const SearchOverlay: React.FC<SearchOverlayProps> = ({
  open,
  onOpenChange,
  isGlobalDarkMode,
}) => {
  const navigate = useNavigate();

  const handleSelect = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search navigation..." />
      <CommandList className={isGlobalDarkMode ? 'bg-sidebar text-sidebar-foreground' : 'bg-white'}>
        <CommandEmpty>No results found.</CommandEmpty>
        
        <CommandGroup heading="Main Navigation">
          {defaultNavigationItems.map((item) => (
            <CommandItem
              key={item.id}
              onSelect={() => handleSelect(item.path)}
              className="flex items-center gap-2"
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {defaultNavigationItems.map((parent) => (
          parent.hasSubItems && parent.subItems && (
            <React.Fragment key={parent.id}>
              <CommandSeparator />
              <CommandGroup heading={parent.label}>
                {parent.subItems.map((subItem) => (
                  <CommandItem
                    key={subItem.id}
                    onSelect={() => handleSelect(subItem.path)}
                    className="flex items-center gap-2"
                  >
                    <subItem.icon className="w-4 h-4" />
                    <span>{subItem.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </React.Fragment>
          )
        ))}

        <CommandSeparator />
        <CommandGroup heading="Settings & Account">
          <CommandItem
            onSelect={() => handleSelect('?modal=settings&tab=profile')}
            className="flex items-center gap-2"
          >
            <User className="w-4 h-4" />
            <span>Profile Settings</span>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect('?modal=settings&tab=workspace')}
            className="flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            <span>Workspace Settings</span>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect('?modal=settings&tab=workspace&workspaceTab=members')}
            className="flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            <span>Manage Members</span>
          </CommandItem>
          <CommandItem
            onSelect={() => handleSelect('?modal=settings&tab=billing')}
            className="flex items-center gap-2"
          >
            <PieChart className="w-4 h-4" />
            <span>Plans & Pricing</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
